export default {
  // Grey general note (restored per PM video) — the full one-per-order rule, shown on the
  // offer sheet and the promo/gift modal. Automatic discounts (smart pricing) still stack.
  general_note:
    "Promo codes and gift cards can't be combined with any other discount. An offer and a points redemption can be used together. Automatic discounts such as the pickup discount still apply.",
  // Full note — offer detail sheet + promo/gift entry (CTO shortened to one line, 2026-07-31).
  full_note: 'Promo codes and gift cards cannot be combined with any other discount.',
  // Green status pill on the offer sheet when the offer's requirement is met (prototype).
  unlocked: 'Unlocked',
  // Short static exclusion note per surface (card footers, CTO-confirmed copy).
  footer: {
    offer: 'Cannot be combined with promo codes or gift cards.',
    promo: 'Cannot be combined with offers, gift cards or points.',
    gift: 'Cannot be combined with offers, promo codes or points.',
  },
  // State-aware conflict line — names the discount that will be displaced.
  // Full mutual exclusivity (PM video): offer, promo, gift and points all block each other.
  conflict: {
    offer_blocked_by_promo: 'A promo code is applied. Applying this offer will remove it.',
    offer_blocked_by_gift: 'A gift card is applied. Applying this offer will remove it.',
    offer_blocked_by_points: 'A points redemption is active. Applying this offer will remove it.',
    promo_gift_displaces_offer: 'Applying a promo code or gift card will remove your active offer.',
    promo_gift_displaces_points: 'Applying a promo code or gift card will remove your points redemption.',
    promo_gift_displaces_offer_and_points:
      'Applying a promo code or gift card will remove your active offer and points redemption.',
  },
  // Comparison-choice card (Inc 4, FOO-2567) — offer / promo detail modals when a conflicting
  // discount is already applied. Lets the customer pick, flagging the higher saving as best.
  comparison: {
    active_line: 'This {{kind}} is already active: {{name}} ({{saving}}).',
    active_line_no_saving: 'This {{kind}} is already active: {{name}}.',
    candidate_line: 'The {{kind}} saves {{saving}}.',
    only_one: 'You can only use one.',
    best_choice: 'Best choice',
    keep: 'Keep {{label}}',
    use_instead: 'Use {{label}} instead',
    kinds: {
      offer: 'offer',
      promo: 'promo code',
      gift: 'gift card',
      points: 'points redemption',
    },
    // Concise nouns for the Keep/Use buttons ("Keep offer", "Use code instead").
    button_kinds: {
      offer: 'offer',
      promo: 'code',
      gift: 'gift card',
      points: 'points',
    },
    // Single-action variant (offer modal, promo/gift already active): one sentence + only the
    // "Use offer instead" button — closing the window keeps the code (CTO 2026-08-03).
    single_message:
      '{{active}} is already active. Only one discount can be used per order — use this offer instead, or close this window to keep your code.',
    active_named: {
      promo: 'Promo code {{name}} ({{saving}})',
      gift: 'Gift card ({{saving}})',
    },
    active_named_no_saving: {
      promo: 'Promo code {{name}}',
      gift: 'Gift card',
    },
    // Multiple active offers (item 6) — promo modal comparison, replaces the active/candidate lines.
    active_offers_two:
      'You have {{count}} active offers: {{first}} ({{firstValue}}) and {{second}} ({{secondValue}}) — {{total}} together. Promo code {{code}} saves {{codeValue}} on this order. Only one can be used.',
    active_offers_more:
      'You have {{count}} active offers: {{first}} ({{firstValue}}), {{second}} ({{secondValue}}) and {{more}} more — {{total}} together. Promo code {{code}} saves {{codeValue}} on this order. Only one can be used.',
  },
  // Discount-card status tag (deals carousel — offer + promo cards), Inc 3.
  card: {
    available: 'Available now',
    applied: 'Applied · {{savings}} off',
    applied_plain: 'Applied',
    replaces: 'Cannot be combined with {{label}} · Replaces {{name}}',
    replaces_no_name: 'Cannot be combined with {{label}}',
    // Prototype card treatment (blocked-by-conflict): a warning line naming the active discount,
    // the one-per-order note, and a "Replaces …" line.
    cannot_combine: 'Cannot be combined with {{name}}',
    replaces_name: 'Replaces {{name}}',
    // Multiple active offers (n>1) — show a count + total instead of a single offer name (item 6).
    cannot_combine_offers: "Can't be combined with your {{count}} active offers",
    replaces_offers: 'Replaces {{count}} active offers ({{total}})',
    // Generic type label for the active discount that would be displaced.
    kind: {
      offer: 'an offer',
      promo: 'a promo code',
      gift: 'a gift card',
      points: 'your points redemption',
    },
  },
  // DisplacementNotice (cart drawer + checkout) — bidirectional & entity-named (PM video).
  // One discount displaced another; the message names both, and Undo restores the displaced one.
  // e.g. "Promo code CHEVERE applied. This removed the 2-für-1 offer. [Undo]".
  displacement: {
    message: '{{applied}} applied. This removed {{removed}}.',
    // Multiple offers removed at once (item 6) — names the top two + the total, in cart + checkout.
    offer_removed_multi: {
      promo:
        'Promo code {{code}} ({{codeValue}}) applied. This removed {{count}} offers: {{first}} and {{second}} ({{total}}).',
      gift: 'Gift card applied. This removed {{count}} offers: {{first}} and {{second}} ({{total}}).',
    },
    undo: 'Undo',
    // The discount that is currently applied (the "winner").
    applied: {
      offer: 'Offer {{name}}',
      promo: 'Promo code {{code}}',
      gift: 'Gift card',
      points: '{{count}} points',
    },
    // The discount that was displaced (the "loser"), restored by Undo.
    removed: {
      offer: 'the {{name}} offer',
      promo: 'the {{code}} promo code',
      gift: 'the gift card',
      points: 'your points redemption',
    },
    // UX feedback item 3 — points redemption displacing a promo/gift. Layer A: a zero-friction hint
    // under the "Use your points" header, shown only while a code/gift is active (before they tap).
    points_hint: {
      promo: 'Using points will remove promo code {{name}} (–{{saving}})',
      gift: 'Using points will remove gift card (–{{saving}})',
      promo_no_saving: 'Using points will remove promo code {{name}}',
      gift_no_saving: 'Using points will remove gift card',
    },
    // Layer B: the post-action displacement notice, extended to the points→code direction.
    points_displaced: {
      promo: '{{points}} points applied (–{{pointsSaving}}). Promo code {{name}} (–{{couponSaving}}) was removed.',
      gift: '{{points}} points applied (–{{pointsSaving}}). Gift card (–{{couponSaving}}) was removed.',
      promo_no_saving: '{{points}} points applied (–{{pointsSaving}}). Promo code {{name}} was removed.',
      gift_no_saving: '{{points}} points applied (–{{pointsSaving}}). Gift card was removed.',
    },
  },
};
